/**
 * Save a message attachment (photo or video) to the viewer's device, right
 * from the thread - Bobbie: "we need to be able to download the photo right
 * there. and save it."
 *
 * Order of preference:
 *
 *   1. iPhone/iPad: Web Share API with the actual file. The share sheet's
 *      first-class options are "Save Image" / "Save Video" (camera roll) and
 *      "Save to Files" - the only road a web app has to the camera roll,
 *      which is what a dance parent means by "save it". Scoped to iOS on
 *      purpose: on a desktop the same API opens a share dialog with no
 *      obvious save target, when what a desktop user wants is a download.
 *
 *   2. Everywhere else: fetch the bytes and click a temporary
 *      <a download> pointed at a blob object URL. A plain
 *      <a download href="https://firebasestorage...."> would NOT work - the
 *      download attribute is ignored on cross-origin URLs, so the browser
 *      navigates to the image instead of saving it. The blob URL is
 *      same-origin, which is what makes `download` stick.
 *
 * A failed fetch is reported in place. It never opens the signed URL in a
 * new tab BY ITSELF: that was both a dead-end instruction inside the viewer
 * and a surprising navigation after an asynchronous save attempt. The tab is
 * still reachable, as a control the reader taps from the failure message
 * (AttachmentSaveButton in components/MessageAttachments), because on iOS
 * opening the original and pressing and holding it is the working route to
 * the camera roll once the blob fetch is out. openInNewTab below is that
 * rung; it is exported for the tap, not called from the ladder.
 *
 * Closing the share sheet without picking anything (AbortError) counts as
 * done, not an error - they saw the sheet and chose to back out.
 */

export type SaveAttachmentOutcome = 'shared' | 'downloaded' | 'opened' | 'dismissed';
export type SaveBlobOutcome = Exclude<SaveAttachmentOutcome, 'opened'>;

/** A fetched file retained briefly when iOS says the first tap expired. */
export interface PreparedBlobSave {
  blob: Blob;
  fileName: string;
  declaredContentType?: string;
}

/**
 * The Web Share API rejected only because transient user activation was no
 * longer available. Callers may retain `prepared` briefly and retry delivery
 * as the first act of one fresh tap, without fetching the file again.
 */
export class SaveNeedsFreshTapError extends Error {
  readonly prepared: PreparedBlobSave;

  constructor(prepared: PreparedBlobSave) {
    super('Tap Save once more to open the share sheet.');
    this.name = 'SaveNeedsFreshTapError';
    this.prepared = prepared;
  }
}

export interface BlobDeliveryOptions {
  /** Surface NotAllowedError instead of falling through to a Files download. */
  deferOnNotAllowed?: boolean;
}

const PHOTO_DOWNLOAD_ENDPOINT = '/.netlify/functions/photoDownload';

// safeHubStorageDownloadUrl moved to hubStorageUrl.ts (security-audit-
// 2026-09-16 finding H-2 round 2, P1-A): the Storage reconcile, a Netlify
// function with no "dom" lib in its tsconfig, needed the same parser
// WITHOUT this file's navigator/window/document usage coming along with it.
// Imported (for use below) and re-exported (so every existing caller of
// THIS file keeps working) rather than duplicated.
import { safeHubStorageDownloadUrl } from './hubStorageUrl.ts';
export { safeHubStorageDownloadUrl } from './hubStorageUrl.ts';

export interface SafeLegacyClassPostMedia {
  url: string;
  storagePath: string;
}

function safeMessageDocumentId(value: string): string | null {
  const id = value.trim();
  return id && id === value && id.length <= 512 && !id.includes('/') && !id.includes('\\')
    && !/[\u0000-\u001f\u007f]/.test(id)
    ? id
    : null;
}

/**
 * Sanitize the pre-attachment-schema imageUrl/videoUrl fields on class posts.
 * These fields have no stored storagePath, so bind the decoded object to this
 * exact class and to the small set of historical message-media folders.
 */
export function safeLegacyClassPostMedia(input: {
  url?: string | null;
  offeringId: string;
  kind: 'image' | 'video';
}): SafeLegacyClassPostMedia | null {
  const offeringId = safeMessageDocumentId(input.offeringId);
  const parsed = safeHubStorageDownloadUrl(input.url);
  if (!offeringId || !parsed) return null;
  const sharedPrefixes = [
    `class-board-attachments/${offeringId}/`,
    `dancer-class-board-attachments/${offeringId}/`,
  ];
  const legacyPrefixes = input.kind === 'image'
    ? [`class-post-images/${offeringId}/`]
    // security-audit-2026-09-16 finding H-3 (round 2 send-back, P1-B): a
    // staff video upload moved off class-media/ onto the gateway-backed
    // staff-class-media-attachments/, but this allowlist was never updated,
    // so a new staff "New video posted" announcement rendered no player.
    // class-media/ stays for every legacy row already written under it.
    : [
      `family-class-videos/${offeringId}/`,
      `class-media/${offeringId}/`,
      `staff-class-media-attachments/${offeringId}/`,
    ];
  return [...sharedPrefixes, ...legacyPrefixes].some((prefix) => (
    parsed.storagePath.startsWith(prefix)
  )) ? parsed : null;
}

/** Backward-compatible URL-only view for existing legacy media callers. */
export function safeLegacyClassPostMediaUrl(input: {
  url?: string | null;
  offeringId: string;
  kind: 'image' | 'video';
}): string | null {
  return safeLegacyClassPostMedia(input)?.url ?? null;
}

/** Exact sanitizer for the one-photo legacy bulletin field. */
export function safeLegacyBulletinImageUrl(input: {
  url?: string | null;
  bulletinId: string;
}): string | null {
  const bulletinId = safeMessageDocumentId(input.bulletinId);
  const parsed = safeHubStorageDownloadUrl(input.url);
  return bulletinId && parsed
    && parsed.storagePath.startsWith(`bulletin-images/${bulletinId}/`)
    ? parsed.url
    : null;
}

/**
 * Return the canonical URL only when an uploaded Hub attachment is safe to
 * render or save.
 *
 * Firestore is long-lived data. Even though every new attachment write is
 * server-authorized, an old or manually seeded document must not be able to
 * make a viewer's browser request javascript:, data:, or an outside tracking
 * URL. Uploaded attachment descriptors therefore need all of these:
 *
 * - HTTPS on the exact Firebase Storage download host
 * - this project's real bucket
 * - alt=media plus a non-empty download token
 * - no extra query fields, credentials, port, or fragment
 * - an object path exactly matching the descriptor's storagePath
 */
export function safeRenderableAttachmentUrl(input: {
  url: string;
  storagePath?: string;
}): string | null {
  const storagePath = input.storagePath?.trim() ?? '';
  if (
    !storagePath
    || storagePath.length > 1024
    || storagePath.startsWith('/')
    || storagePath.includes('\\')
    || /[\u0000-\u001f\u007f]/.test(storagePath)
    || storagePath.split('/').some((part) => part === '' || part === '.' || part === '..')
  ) {
    return null;
  }

  const parsed = safeHubStorageDownloadUrl(input.url);
  return parsed?.storagePath === storagePath ? parsed.url : null;
}

const EXTENSION_BY_TYPE: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
  'video/3gpp': '3gp',
  // The plain-file lane (isMessageFileContentType in lib/threads.ts).
  'application/pdf': 'pdf',
  'audio/mpeg': 'mp3',
  'audio/mp4': 'm4a',
  'audio/x-m4a': 'm4a',
  'audio/wav': 'wav',
  'audio/aac': 'aac',
  'audio/ogg': 'ogg',
  'text/plain': 'txt',
  'text/csv': 'csv',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
};

// The only extensions a stored attachment name may keep. Anything else gets
// a media extension appended: attachment names live in a Firestore doc any
// active participant can write, and saving "invoice.exe" verbatim would hand
// a hand-crafted message doc a one-tap download-under-attacker-chosen-
// extension primitive on every recipient's device.
const KEEPABLE_EXTENSIONS = new Set([
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif',
  'mp4', 'mov', 'm4v', 'webm', '3gp', 'avi',
  // The plain-file lane. Deliberately NO html/svg/zip/exe - those are the
  // extensions the allowlist exists to keep out of a one-tap download.
  'pdf', 'mp3', 'm4a', 'wav', 'aac', 'ogg', 'txt', 'csv', 'docx', 'xlsx',
]);

/**
 * "IMG 0012" + image/jpeg -> "IMG 0012.jpg". Keeps a name that already ends
 * in a known MEDIA extension as-is; without one, iOS quietly refuses to
 * offer "Save Image" and a desktop download lands as an extensionless
 * mystery file - and a non-media extension is never kept (see above).
 */
export function attachmentFileName(name: string, contentType: string): string {
  const type = (contentType || '').toLowerCase();
  const fallbackNoun = type.startsWith('video/')
    ? 'video'
    : type.startsWith('audio/')
      ? 'audio'
      : type.startsWith('image/') || !type
        ? 'photo'
        : 'file';
  const trimmed = (name || '').trim() || fallbackNoun;
  const existing = /\.([a-z0-9]{2,4})$/i.exec(trimmed);
  if (existing && KEEPABLE_EXTENSIONS.has(existing[1].toLowerCase())) return trimmed;
  const ext =
    EXTENSION_BY_TYPE[type] ||
    (type.startsWith('video/') ? 'mp4' : type.startsWith('audio/') ? 'm4a' : type === 'application/pdf' ? 'pdf' : 'jpg');
  return `${trimmed}.${ext}`;
}

/**
 * iPhone/iPad, including iPadOS masquerading as macOS (MacIntel + touch).
 * Exported for lib/saveVideo.ts, whose R2 route must fetch the bytes for the
 * share sheet on exactly these devices and nowhere else.
 */
export function isIosDevice(): boolean {
  if (/iPad|iPhone|iPod/.test(navigator.userAgent)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

/**
 * Hand a Blob we already hold to the device, by the ladder in this file's
 * header: iOS share sheet first (the only road to the camera roll), then a
 * temporary `<a download>` on a same-origin blob URL.
 *
 * SHARED, and deliberately so. It was the tail of the message attachment save
 * until 2026-08-28, when video links gained their own Save control
 * (lib/saveVideo.ts, for Bobbie's *"Parents are asking to be able to save the
 * videos"*). Two callers now need the same ladder, and it is the kind of
 * code that must not exist twice: the `download` attribute being ignored
 * cross-origin, and the revoke needing a delay in WebKit, are both bugs you
 * only find once and then have to keep fixed in every copy.
 *
 * `declaredContentType` is the type the STUDIO recorded for the file, used
 * when the fetched blob does not carry one of its own. Dropping it was a real
 * regression when this was factored out: a stored object served without a
 * Content-Type reached the iOS share sheet as application/octet-stream, and
 * the share sheet then offers no "Save Video" - which is the one outcome the
 * whole ladder exists to reach.
 */
export async function deliverPreparedBlobToDevice(
  prepared: PreparedBlobSave,
  options: BlobDeliveryOptions = {},
): Promise<SaveBlobOutcome> {
  const file = new File([prepared.blob], prepared.fileName, {
    type: prepared.blob.type || prepared.declaredContentType || 'application/octet-stream',
  });

  const nav = navigator as Navigator & {
    canShare?: (data: ShareData) => boolean;
    share?: (data: ShareData) => Promise<void>;
  };
  if (
    isIosDevice() &&
    typeof nav.share === 'function' &&
    typeof nav.canShare === 'function' &&
    nav.canShare({ files: [file] })
  ) {
    try {
      await nav.share({ files: [file] });
      return 'shared';
    } catch (e) {
      if ((e as { name?: string } | null)?.name === 'AbortError') return 'dismissed';
      if (
        (e as { name?: string } | null)?.name === 'NotAllowedError'
        && options.deferOnNotAllowed
      ) {
        throw new SaveNeedsFreshTapError(prepared);
      }
      // A non-activation share failure still gets the download path below.
      // On a fresh retry, a second NotAllowedError also lands there instead
      // of asking for taps forever.
    }
  }

  const objectUrl = URL.createObjectURL(prepared.blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = prepared.fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Give the click a beat to hand the blob to the download machinery before
  // the URL dies; revoking synchronously loses the download in some WebKit.
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
  return 'downloaded';
}

export async function deliverBlobToDevice(
  blob: Blob,
  fileName: string,
  declaredContentType?: string,
  options: BlobDeliveryOptions = {},
): Promise<SaveBlobOutcome> {
  return deliverPreparedBlobToDevice({
    blob,
    fileName,
    ...(declaredContentType ? { declaredContentType } : {}),
  }, options);
}

/**
 * Open a URL in a new tab, where long-press / right-click save still works.
 * The last rung of the ladder, shared for the same reason as the one above.
 *
 * NOT window.open(..., 'noopener'): with noopener in the feature string the
 * spec makes window.open return null even on SUCCESS, which reads as "popup
 * blocked" every time. Open plainly, then sever the opener reference on the
 * window we got back; null now really means blocked.
 */
export function openInNewTab(url: string): boolean {
  const opened = window.open(url, '_blank');
  if (!opened) return false;
  opened.opener = null;
  return true;
}

interface AttachmentBlobFetchDeps {
  fetcher?: typeof fetch;
  proxyHeaders?: () => Promise<Record<string, string>>;
}

/**
 * Fetch an attachment's bytes, with a same-origin recovery path for photos.
 *
 * A Firebase Storage URL can render in an <img> while fetch() of that same
 * URL still fails in a particular browser, privacy mode, or filtered network.
 * Saving needs readable bytes for both the iOS share sheet and the desktop
 * blob download, so a displayed photo otherwise ends in a dead Save button.
 * The photoDownload function is deliberately only a second attempt: it
 * validates this same tokenized LDG object again, caps it at the upload
 * limit, and refuses non-image bytes.
 */
export async function fetchAttachmentBlob(
  input: {
    url: string;
    contentType: string;
    storagePath?: string;
  },
  deps: AttachmentBlobFetchDeps = {},
): Promise<Blob> {
  const safeUrl = safeRenderableAttachmentUrl(input);
  if (!safeUrl) throw new Error('This attachment is not available.');
  const fetcher = deps.fetcher ?? fetch;

  try {
    const direct = await fetcher(safeUrl);
    if (!direct.ok) throw new Error(`fetch failed (${direct.status})`);
    return await direct.blob();
  } catch {
    if (!input.contentType.toLowerCase().startsWith('image/')) {
      throw new Error('Could not reach the file.');
    }
  }

  try {
    const headers = deps.proxyHeaders
      ? await deps.proxyHeaders()
      : await import('./functionSecurity').then(({ publicFunctionHeaders }) => publicFunctionHeaders());
    const response = await fetcher(PHOTO_DOWNLOAD_ENDPOINT, {
      method: 'POST',
      headers,
      body: JSON.stringify({ url: safeUrl, storagePath: input.storagePath }),
    });
    if (!response.ok) throw new Error(`photo recovery failed (${response.status})`);
    const blob = await response.blob();
    if (!blob.type.toLowerCase().startsWith('image/')) throw new Error('photo recovery returned non-image bytes');
    return blob;
  } catch {
    throw new Error('Could not reach the file.');
  }
}

/**
 * The network half of a message attachment save: fetch the bytes and name
 * the file. AttachmentSaveButton (components/MessageAttachments) delivers the
 * result with deliverPreparedBlobToDevice, and keeps it for one fresh tap
 * only when iOS refuses the share because the first tap expired.
 */
export async function prepareAttachmentForDevice(input: {
  url: string;
  name: string;
  contentType: string;
  storagePath?: string;
}): Promise<PreparedBlobSave> {
  const blob = await fetchAttachmentBlob(input);
  return {
    blob,
    fileName: attachmentFileName(input.name, blob.type || input.contentType),
    declaredContentType: input.contentType,
  };
}
